import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { providerFullDisplayName } from "@pontimed/types";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useLogout, useSession } from "@pontimed/auth-client";
import { BeamFrame } from "@pontimed/ui/puls";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Clock,
  Inbox,
  Loader2,
  Lock,
  MapPin,
  Send,
  ShieldCheck,
  Stethoscope,
  Video,
  Zap,
} from "lucide-react";

import { useAuthModal } from "@/components/auth/auth-modal-context";
import { apiErrorCode } from "@/components/booking-request/api-errors";
import { formatDateList, type TokenLanding } from "@/components/booking-request/types";
import { ProviderAvatar } from "@/components/booking/booking-ui";
import { DpaClickwrap } from "@/components/consent/dpa-clickwrap";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PhoneInput } from "@/components/ui/phone-input";
import { Skeleton } from "@/components/ui/skeleton";
import { findCountryPhone } from "@/config/phone-prefixes";
import { useNoIndexMeta } from "@/lib/hooks/use-noindex-meta";
import { showToast } from "@/lib/ui/toast";

const SUPPORT_EMAIL = "support@pontimed.app";

/* ─── Puls page shell ───
   Same language as the apps/web Puls kit (`components/puls/primitives.tsx`,
   not importable from apps/app): blueprint grid + soft glow behind one
   card, toned icon tiles, display headings, mono labels, 48px buttons.
   Presentational only. */

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 70% 55% at 50% 25%, black 15%, transparent 72%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};
/** Soft primary wash at the top of the card. */
const CARD_WASH: CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, color-mix(in oklch, var(--primary-soft) 55%, var(--card)) 0%, var(--card) 30%)",
};
const PAGE_CARD =
  "border-border bg-card w-full max-w-xl self-start rounded-2xl border p-5 shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)] sm:p-7";
const TITLE =
  "puls-reveal font-display text-foreground m-0 text-2xl leading-tight font-semibold tracking-[-0.025em] text-balance";
/** `.puls-reveal` stagger steps (80 ms each, see puls.css). */
const STAGGER_1 = { "--puls-i": 1 } as CSSProperties;
const STAGGER_2 = { "--puls-i": 2 } as CSSProperties;
const BTN = "h-12 w-full gap-2 rounded-lg px-6 text-sm font-semibold";
const BTN_PRIMARY = `${BTN} hover:bg-primary-hover`;
const BTN_SECONDARY = `${BTN} bg-card text-foreground hover:border-primary/40 hover:bg-card hover:text-primary`;
const ERROR_BOX =
  "border-destructive-border bg-destructive-soft text-destructive-strong m-0 rounded-lg border px-3 py-2.5 text-sm";
const FIELD_LABEL =
  "text-muted-foreground mb-1.5 block font-mono text-[11px] font-medium tracking-[0.12em] uppercase";

function PageBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-0 left-1/2 h-[min(60%,36rem)] w-[min(92%,48rem)] -translate-x-1/2 blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}

/**
 * Public landing page for the doctor-link token we email / text when a
 * patient sends a booking request to a provider that isn't on PontiMed yet
 * (`_docs/booking-requests-unclaimed-providers.md` §5.4).
 *
 * Sits at root — the token IS the authentication for reading the (PHI-free)
 * request summary. Answering it requires a real session, which is what the
 * "activate" flow creates.
 */
export const Route = createFileRoute("/requests/$token")({
  component: BookingRequestLandingPage,
});

type ActivateOutcome =
  | { kind: "identity_mismatch"; channel: "email" | "sms" | null; maskedContact: string | null }
  | { kind: "claimed_by_other" }
  | { kind: "manual_claim_required" }
  | { kind: "error"; message: string };

function BookingRequestLandingPage() {
  const { token } = Route.useParams();
  const { i18n, t } = useLingui();
  const locale = i18n.locale;

  useNoIndexMeta();

  const query = useQuery<TokenLanding, Error>({
    queryKey: ["booking-request-token", token],
    queryFn: () =>
      apiFetch.get<TokenLanding>(`/api/booking-requests/token/${encodeURIComponent(token)}`, {
        skipAuthRefresh: true,
      }),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.status === 404 || error.status === 410)) &&
      failureCount < 2,
  });

  return (
    <main className="relative isolate flex min-h-dvh justify-center px-4 py-8 sm:py-14">
      <PageBackdrop />
      <div className={PAGE_CARD} style={CARD_WASH}>
        {query.isLoading && <LandingSkeleton />}

        {!query.isLoading && query.isError && (
          <TokenError
            expired={query.error instanceof ApiError && query.error.status === 410}
            message={
              query.error instanceof ApiError && query.error.status === 404
                ? null
                : query.error instanceof ApiError && query.error.status === 410
                  ? null
                  : t`Please check your connection and try again.`
            }
          />
        )}

        {!query.isLoading && query.data && (
          <LandingBody data={query.data} token={token} locale={locale} />
        )}
      </div>
    </main>
  );
}

function LandingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Skeleton className="size-14 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-3 w-28" />
        </div>
      </div>
      <Skeleton className="h-56 w-full rounded-2xl" />
      <Skeleton className="h-12 w-full rounded-lg" />
    </div>
  );
}

function TokenError({ expired, message }: { expired: boolean; message: string | null }) {
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <span
        aria-hidden="true"
        className="puls-reveal border-destructive-border bg-destructive-soft text-destructive grid size-14 shrink-0 place-items-center rounded-2xl border"
      >
        <AlertCircle className="size-6" />
      </span>
      <h1 className={TITLE} style={STAGGER_1}>
        {expired ? <Trans>This link has expired</Trans> : <Trans>Link not found</Trans>}
      </h1>
      <p
        className="puls-reveal text-muted-foreground m-0 max-w-sm text-sm leading-relaxed"
        style={STAGGER_2}
      >
        {message ??
          (expired ? (
            <Trans>
              Booking-request links stay valid for 30 days. Open the most recent message we sent
              you, or contact us and we'll resend it.
            </Trans>
          ) : (
            <Trans>The link may be incomplete. Open it again from the original message.</Trans>
          ))}
      </p>
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="text-primary inline-flex min-h-[44px] items-center font-mono text-sm font-medium underline-offset-4 hover:underline"
      >
        {SUPPORT_EMAIL}
      </a>
    </div>
  );
}

function LandingBody({
  data,
  token,
  locale,
}: {
  data: TokenLanding;
  token: string;
  locale: string;
}) {
  const providerName = providerFullDisplayName(data.provider, locale);
  const specialtyName = data.provider.specialtyName
    ? (data.provider.specialtyName[locale] ??
      data.provider.specialtyName.en ??
      data.provider.specialtyName.ro ??
      data.provider.specialtyCode)
    : data.provider.specialtyCode;

  return (
    <div className="space-y-5">
      <header className="puls-reveal puls-ring-host flex items-center gap-4">
        <ProviderAvatar provider={data.provider} alt="" className="size-14" />
        <div className="min-w-0">
          <h1 className="font-display text-foreground m-0 truncate text-xl leading-tight font-semibold tracking-[-0.02em] sm:text-2xl">
            {providerName}
          </h1>
          <p className="text-muted-foreground m-0 mt-1 truncate text-sm">
            {[specialtyName, data.provider.primaryCity].filter(Boolean).join(" · ")}
          </p>
        </div>
      </header>

      <RequestCard data={data} locale={locale} />

      {data.recipientKind === "facility" ? (
        <FacilityRelaySection data={data} token={token} />
      ) : (
        <ProviderSection data={data} token={token} />
      )}
    </div>
  );
}

/* ─── Request summary — PHI-free by contract (D4) ─── */

function RequestCard({ data, locale }: { data: TokenLanding; locale: string }) {
  const { request } = data;
  const expires = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(request.expiresAt));

  const preferred =
    request.preferredDates.length > 0 ? formatDateList(request.preferredDates, locale) : null;

  return (
    <section
      className="puls-reveal border-border bg-background/60 block overflow-hidden rounded-2xl border"
      style={STAGGER_1}
    >
      <div className="border-border flex items-start justify-between gap-3 border-b border-dashed p-4">
        <p className="text-foreground m-0 text-sm font-semibold">
          <Trans>A patient asked for an appointment</Trans>
        </p>
        <StatusBadge status={request.status} />
      </div>

      <dl className="m-0 space-y-3 p-4 text-sm">
        <Row
          icon={request.consultationType === "video" ? <Video /> : <Stethoscope />}
          label={<Trans>Type</Trans>}
        >
          {request.consultationType === "video" ? (
            <Trans>Video consultation</Trans>
          ) : (
            <Trans>At the office</Trans>
          )}
        </Row>
        {request.patientCity && (
          <Row icon={<MapPin />} label={<Trans>Patient city</Trans>}>
            {request.patientCity}
          </Row>
        )}
        <Row icon={<CalendarClock />} label={<Trans>Preferred</Trans>}>
          {preferred ?? <Trans>As soon as possible</Trans>}
          {request.preferredTimeOfDay !== "any" && (
            <>
              {" · "}
              <TimeOfDay id={request.preferredTimeOfDay} />
            </>
          )}
        </Row>
        <Row icon={<Zap />} label={<Trans>Urgency</Trans>}>
          {request.urgency === "urgent" ? <Trans>Urgent</Trans> : <Trans>Routine</Trans>}
        </Row>
        <Row icon={<Clock />} label={<Trans>Expires on</Trans>}>
          {expires}
        </Row>
      </dl>

      <p className="border-border text-muted-foreground m-0 flex items-start gap-2 border-t border-dashed px-4 py-3 text-xs leading-relaxed">
        <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <span>
          <Trans>
            The patient's name and the reason for the visit appear only inside your PontiMed
            account.
          </Trans>
        </span>
      </p>
    </section>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon: ReactElement;
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className="bg-muted text-muted-foreground grid size-8 shrink-0 place-items-center rounded-lg [&>svg]:size-4"
      >
        {icon}
      </span>
      <dt className="text-muted-foreground font-mono text-[11px] tracking-[0.12em] uppercase">
        {label}
      </dt>
      <dd className="text-foreground m-0 min-w-0 flex-1 text-right text-sm font-medium">
        {children}
      </dd>
    </div>
  );
}

function TimeOfDay({ id }: { id: string }) {
  if (id === "morning") return <Trans>Morning</Trans>;
  if (id === "afternoon") return <Trans>Afternoon</Trans>;
  if (id === "evening") return <Trans>Evening</Trans>;
  return <Trans>Any time</Trans>;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "pending")
    return (
      <Badge variant="warning">
        <Clock className="size-3" />
        <Trans>Waiting for you</Trans>
      </Badge>
    );
  if (status === "accepted")
    return (
      <Badge variant="success">
        <CheckCircle2 className="size-3" />
        <Trans>Accepted</Trans>
      </Badge>
    );
  if (status === "declined")
    return (
      <Badge variant="neutral">
        <Trans>Declined</Trans>
      </Badge>
    );
  if (status === "cancelled")
    return (
      <Badge variant="neutral">
        <Trans>Cancelled by the patient</Trans>
      </Badge>
    );
  if (status === "closed")
    return (
      <Badge variant="neutral">
        <Trans>Closed</Trans>
      </Badge>
    );
  return (
    <Badge variant="neutral">
      <Trans>Expired</Trans>
    </Badge>
  );
}

/* ─── Doctor branch ─── */

function ProviderSection({ data, token }: { data: TokenLanding; token: string }) {
  const { t } = useLingui();
  const { status } = useSession();
  const auth = useAuthModal();
  const { logout } = useLogout();

  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<ActivateOutcome | null>(null);

  const returnTo =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : `/requests/${token}`;

  const openAuth = () => auth.open({ intent: "booking-request", returnTo });

  if (data.request.status !== "pending") {
    return (
      <p className="bg-muted text-muted-foreground m-0 rounded-xl px-4 py-3 text-sm leading-relaxed">
        <Trans>
          This request is closed — nothing left to do here. You can still activate your PontiMed
          profile to receive the next one.
        </Trans>
      </p>
    );
  }

  const claimWizardHref = `/account/user/dashboard?add=provider&aMode=claim&claimId=${data.provider.id}`;

  // No auto-claim — either AUTO_CLAIM_VIA_CONTACT_CHANNEL is off, or the
  // address came from the clinic's relay (`reason: "relay_contact"`) and so
  // proves nothing about the doctor. The document wizard is the only way in.
  if (!data.provider.isClaimed && !data.claim.canAutoClaim) {
    return (
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm">
          {data.claim.reason === "relay_contact" ? (
            <Trans>
              This address was given to us by the clinic, so we can't activate the profile from it
              directly. Claim the profile with your documents — it takes a few minutes.
            </Trans>
          ) : (
            <Trans>
              To answer this request, claim your profile with your registration documents. It takes
              a few minutes and unlocks the full provider workspace.
            </Trans>
          )}
        </p>
        <Button className={BTN_PRIMARY} onClick={() => window.location.assign(claimWizardHref)}>
          <ShieldCheck className="size-4" />
          <Trans>Claim with documents</Trans>
        </Button>
      </div>
    );
  }

  // Already claimed: the doctor just needs to sign in; the request is waiting
  // in their provider inbox.
  if (data.provider.isClaimed) {
    return (
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm">
          <Trans>This profile is already active on PontiMed. Sign in to answer the request.</Trans>
        </p>
        {status === "authenticated" ? (
          <Button
            className={BTN_PRIMARY}
            onClick={() => window.location.assign("/account/provider/appointments?tab=requests")}
          >
            <Inbox className="size-4" />
            <Trans>Open my requests</Trans>
          </Button>
        ) : (
          <Button className={BTN_PRIMARY} onClick={openAuth}>
            <Inbox className="size-4" />
            <Trans>Sign in to respond</Trans>
          </Button>
        )}
      </div>
    );
  }

  const activate = async () => {
    setSubmitting(true);
    setOutcome(null);
    const toast = showToast(t`Activating your profile…`, "loading");
    try {
      const res = await apiFetch.post<{ status: string; redirect: string }>(
        `/api/booking-requests/token/${encodeURIComponent(token)}/activate`,
        { dpaAccepted: true },
      );
      toast.update(t`Profile activated`, "success");
      window.location.assign(res.redirect);
    } catch (err) {
      const body = err instanceof ApiError ? ((err.body ?? {}) as Record<string, unknown>) : {};
      const code = apiErrorCode(err);
      if (code === "IDENTITY_MISMATCH") {
        setOutcome({
          kind: "identity_mismatch",
          channel: (body.channel as "email" | "sms" | null) ?? data.claim.channel,
          maskedContact: (body.maskedContact as string | null) ?? data.claim.maskedContact,
        });
        toast.update(t`That account doesn't match the address we wrote to`, "error");
      } else if (code === "CLAIMED_BY_OTHER") {
        setOutcome({ kind: "claimed_by_other" });
        toast.update(t`This profile is already claimed`, "error");
      } else if (code === "MANUAL_CLAIM_REQUIRED") {
        setOutcome({ kind: "manual_claim_required" });
        toast.update(t`Document verification is required`, "error");
      } else {
        const message =
          code === "dpa_acceptance_required"
            ? t`Please accept the Data Processing Agreement first.`
            : t`Could not activate your profile. Please try again.`;
        setOutcome({ kind: "error", message });
        toast.update(message, "error");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        <Trans>
          Activating your free PontiMed profile takes a minute. After that you see the patient's
          details and can accept or decline.
        </Trans>
      </p>

      {outcome?.kind === "identity_mismatch" ? (
        <div className="border-warning-border bg-warning-soft space-y-3 rounded-xl border p-4">
          <p className="text-foreground text-sm font-semibold">
            <Trans>Signed in with a different account</Trans>
          </p>
          <p className="text-muted-foreground text-sm">
            {outcome.channel === "sms" ? (
              <Trans>
                We texted {outcome.maskedContact}. Sign in with that phone number to take over this
                profile, or claim it with documents instead.
              </Trans>
            ) : (
              <Trans>
                We wrote to {outcome.maskedContact}. Sign in with that address to take over this
                profile, or claim it with documents instead.
              </Trans>
            )}
          </p>
          <Button
            className={BTN_PRIMARY}
            onClick={async () => {
              await logout();
              openAuth();
            }}
          >
            <Trans>Sign out and use {outcome.maskedContact}</Trans>
          </Button>
          <Button
            variant="outline"
            className={BTN_SECONDARY}
            onClick={() => window.location.assign(claimWizardHref)}
          >
            <Trans>Claim with documents instead</Trans>
          </Button>
        </div>
      ) : outcome?.kind === "claimed_by_other" ? (
        <div className="border-border bg-muted/60 space-y-2 rounded-xl border p-4">
          <p className="text-foreground text-sm font-semibold">
            <Trans>This profile already belongs to another account</Trans>
          </p>
          <p className="text-muted-foreground text-sm">
            <Trans>
              If that isn't right, write to us and we'll sort it out — include your full name and
              registration number.
            </Trans>
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-primary inline-flex min-h-[44px] items-center font-mono text-sm font-medium underline-offset-4 hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      ) : outcome?.kind === "manual_claim_required" ? (
        <div className="border-border bg-muted/60 space-y-3 rounded-xl border p-4">
          <p className="text-muted-foreground text-sm">
            <Trans>
              This profile needs document verification before it can be activated. The claim wizard
              walks you through it.
            </Trans>
          </p>
          <Button className={BTN_PRIMARY} onClick={() => window.location.assign(claimWizardHref)}>
            <ShieldCheck className="size-4" />
            <Trans>Claim with documents</Trans>
          </Button>
        </div>
      ) : (
        <>
          {status === "authenticated" && (
            <DpaClickwrap checked={dpaAccepted} onChange={setDpaAccepted} party="individual" />
          )}

          {outcome?.kind === "error" && (
            <p className={ERROR_BOX} role="alert">
              {outcome.message}
            </p>
          )}

          <BeamFrame className="rounded-lg" busy={submitting}>
            <Button
              className={BTN_PRIMARY}
              disabled={submitting || (status === "authenticated" && !dpaAccepted)}
              onClick={() => {
                if (status !== "authenticated") {
                  openAuth();
                  return;
                }
                void activate();
              }}
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <ShieldCheck className="size-4" aria-hidden />
              )}
              <Trans>Activate my profile and view the request</Trans>
            </Button>
          </BeamFrame>

          {status !== "authenticated" && data.claim.maskedContact && (
            <p className="text-muted-foreground text-center text-xs">
              {data.claim.channel === "sms" ? (
                <Trans>Sign in with the phone number {data.claim.maskedContact}.</Trans>
              ) : (
                <Trans>Sign in with the address {data.claim.maskedContact}.</Trans>
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Clinic relay branch (D7) ─── */

function FacilityRelaySection({ data, token }: { data: TokenLanding; token: string }) {
  const { t, i18n } = useLingui();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState(
    (data.provider.primaryCountry || "ro").toLowerCase(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fullPhone = phone.trim()
    ? `${findCountryPhone(phoneCountry).prefix}${phone.trim().replace(/\s/g, "")}`
    : "";
  const canSubmit = Boolean(email.trim() || fullPhone) && !submitting;

  const providerName = providerFullDisplayName(data.provider, i18n.locale);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const toast = showToast(t`Sending to the doctor…`, "loading");
    try {
      await apiFetch.post(
        `/api/booking-requests/token/${encodeURIComponent(token)}/relay`,
        {
          email: email.trim() || undefined,
          phone: fullPhone || undefined,
        },
        { skipAuthRefresh: true },
      );
      setSent(true);
      toast.update(t`Sent to the doctor`, "success");
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 429
          ? t`Too many attempts. Please try again later.`
          : t`Could not send it. Check the contact details and try again.`;
      setError(message);
      toast.update(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const claimFacilityHref = data.facility
    ? `/account/user/dashboard?add=facility&aMode=claim&claimId=${data.facility.id}`
    : null;

  if (sent) {
    return (
      <div className="space-y-3">
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <span
            aria-hidden="true"
            className="puls-reveal border-success-border bg-success-soft text-success grid size-14 shrink-0 place-items-center rounded-2xl border"
          >
            <CheckCircle2 className="size-6" />
          </span>
          <p
            className="puls-reveal font-display text-foreground m-0 text-lg leading-tight font-semibold tracking-[-0.015em]"
            style={STAGGER_1}
          >
            <Trans>We sent the request to the doctor</Trans>
          </p>
          <p
            className="puls-reveal text-muted-foreground m-0 max-w-sm text-sm leading-relaxed"
            style={STAGGER_2}
          >
            <Trans>Thank you. {providerName} can now answer the patient directly.</Trans>
          </p>
        </div>
        {claimFacilityHref && (
          <Button
            variant="outline"
            className={BTN_SECONDARY}
            onClick={() => window.location.assign(claimFacilityHref)}
          >
            <Trans>Claim your clinic</Trans>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        <Trans>
          Tell us how to reach {providerName} and we'll forward the request to them. We only use the
          contact for booking requests.
        </Trans>
      </p>

      <div>
        <label htmlFor="relay-email" className={FIELD_LABEL}>
          <Trans>Doctor's email</Trans>
        </label>
        <input
          id="relay-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nume@clinica.ro"
          className="border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary/15 h-12 w-full rounded-lg border px-3 text-sm transition-[border-color,box-shadow] focus:ring-4 focus:outline-none"
        />
      </div>

      <div>
        <label htmlFor="relay-phone" className={FIELD_LABEL}>
          <Trans>…or the doctor's phone</Trans>
        </label>
        <PhoneInput
          id="relay-phone"
          value={phone}
          onChange={setPhone}
          countryCode={phoneCountry}
          onCountryChange={(code) => setPhoneCountry(code)}
        />
      </div>

      {error && (
        <p className={ERROR_BOX} role="alert">
          {error}
        </p>
      )}

      <BeamFrame className="rounded-lg" busy={submitting}>
        <Button className={BTN_PRIMARY} disabled={!canSubmit} onClick={() => void submit()}>
          {submitting ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Send className="size-4" aria-hidden />
          )}
          <Trans>Forward to the doctor</Trans>
        </Button>
      </BeamFrame>

      {claimFacilityHref && (
        <button
          type="button"
          onClick={() => window.location.assign(claimFacilityHref)}
          className="text-muted-foreground hover:text-foreground min-h-[44px] w-full text-center text-xs font-medium underline-offset-4 hover:underline"
        >
          <Trans>Is this your clinic? Claim it for free on PontiMed.</Trans>
        </button>
      )}
    </div>
  );
}
